import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions
} from "@floating-ui/react"
import { Placement } from "@floating-ui/utils"
import { ReactNode, useRef, useState } from "react"

type TooltipProps = {
  content?: ReactNode
  useOverflowStateToShowContent?: boolean
  placement?: Placement
  children: (props: Record<string, any>) => ReactNode
  rootNodeRef?: HTMLElement | null
  defaultWidth?: string
  className?: string
}

const TooltipV2 = ({
  content,
  placement = "top",
  useOverflowStateToShowContent = true,
  children,
  rootNodeRef,
  defaultWidth = "auto",
  className
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const {
    refs: { setReference, setFloating, domReference },
    floatingStyles,
    context
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset({
        alignmentAxis: 0,
        mainAxis: 10
      }),
      flip(),
      shift(),
      arrow({
        element: arrowRef
      })
    ],
    whileElementsMounted: autoUpdate
  })

  const hover = useHover(context, {
    mouseOnly: true,
    handleClose: safePolygon()
  })

  const click = useClick(context, {
    ignoreMouse: true
  })

  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss
  ])

  return (
    <>
      {children({ ref: setReference, ...getReferenceProps() })}

      {isOpen &&
        content &&
        (useOverflowStateToShowContent
          ? (domReference.current?.scrollWidth ?? 0) >
            (domReference.current?.clientWidth ?? 0)
          : true) && (
          <FloatingPortal root={rootNodeRef}>
            <div
              ref={setFloating}
              style={{
                ...floatingStyles,
                width: defaultWidth
              }}
              {...getFloatingProps({
                className: `bg-[#1A1D21] text-[#fff] text-xs font-normal font-jakarta max-w-[300px] border-[0.5px] border-[#4B5563] z-10  ${className}`
              })}
            >
              <div className="py-2 px-3 max-h-[258px] overflow-y-auto">
                {content}
              </div>
              <FloatingArrow
                ref={arrowRef}
                context={context}
                className="fill-[#1A1D21]
                 [&>path:first-of-type]:stroke-[#4B5563]
                [&>path:first-of-type]:stroke-[0.5px]
                [&>path:last-of-type]:stroke-[#4B5563]
                [&>path:last-of-type]:stroke-[0.5px]
                "
                width={16}
                height={10}
              />
            </div>
          </FloatingPortal>
        )}
    </>
  )
}

export default TooltipV2
